// janus-engine/ability/upcast.ts — the SPELL-LEVEL upcast (Session 1 step 5a, 2026-09-29): the sheet's "cast at level N" picker sends N as
// use_ability_variable_1, and upcastRoll() below reads it (dice_upcast /
// mod_upcast × the levels above the spell's own) and template_spell_roll()'s mana debit ("THE INT PASSED IS MANA USED").
// MODELLED: a leveled spell / curse / menta (ft_spell_is_leveled) with a numeric cast variable, with plain-integer dice_upcast /
// mod_upcast, on primary damage, healing, temp-V and resource-gain rolls. HANDED BACK (pre-draw): every other upcast reader —
// non-instant projectiles, upcast duration, a wider area (spell_upcast_increase_aoe_ft) on
// anything but a PLACED area, a tagged (formula) dice_upcast / mod_upcast. Below-base values add no growth (PHP's max(0, diff));
// the same variable can be an amount, including zero charges. A placed area's growth is upcastAreaGrowthFt(): the expansion reads the grown radius and the
// zone takes the FT3D stage's span, which the stage already sizes the same way (FT3DAOEPlacement: + growth × levels above).
// Extra targets are handled by R-103 targetCountRefusal; map-point areas are exempt from that ceiling.
import { type JanusCtx, type Row, handBack, itemField, record } from './context.js';
import { instantProjectileCount } from '../../shared/projectile-targeting.js';
import { layerListEntryUuid } from '../actions/spell-components.js';

const LEVELED_TYPES = ['spell', 'curse', 'menta'];
/** The upcast readers this module does not model: any of them on a cast above the spell's own level hands back. */
const UNMODELLED_UPCAST_LAYERS = ['spell_upcast_additional_projectiles_created',
  'spell_upcast_duration_type', 'spell_upcast_duration_int', 'spell_upcast_increase_aoe_ft'];

/** The roll types whose browser twin applies upcastRoll(): damage / damage over time / contest / spell attack (all through
 *  damage_type_roll), healing, temp V, mana and luck. */
const UPCAST_ROLL_TYPES = ['82BBB3B6-4646-48E8-A5A2-8DC0947A91D0', '77A044B7-7041-CA96-3C63-BB718B15A364', 'AD80D31D-898B-66CE-0894-1335D11B1529',
  '5740CA49-006E-A7A0-B46B-6DDED6CD546C', 'DD280A3B-6F1D-4CE9-9EFD-505A74D0C07C', '4FB7452A-F130-40C3-83E6-5E92C329D7B2', '5E2689A2-0F7D-1E27-F4D5-CA09CA5C2591',
  'DB30C685-3830-9AEA-D8FA-D92FEA762982', 'C823C739-A9DE-AA5A-07B8-CB67F578F880'];

/** layer_value(spell_level) ?? layer_value(menta_level) ?? 0, as upcast_roll() reads it. */
export function spellLevelOf(ctx: JanusCtx, ability: Row): number {
  const raw = ctx.read.value(ability, 'spell_level') ?? ctx.read.value(ability, 'menta_level') ?? 0;
  const n = Number(raw);
  return Number.isFinite(n) ? Math.trunc(n) : 0;
}

/** A numeric use_ability_variable_1 on a leveled spell / curse / menta: the cast level; null when the variable is not a cast level. */
export function upcastLevelVariable(ctx: JanusCtx, ability: Row, variable1: unknown, opts: { placedArea?: boolean } = {}): number | null {
  if (variable1 == null || variable1 === '') return null;
  const text = String(variable1).trim();
  if (!/^\d{1,2}$/.test(text)) return null;
  if (!LEVELED_TYPES.includes(String(itemField(ability, 'item_type') ?? ''))) return null;
  const level = spellLevelOf(ctx, ability);
  if (level <= 0) return null; // a cantrip is never level-cast (ft_spell_is_leveled — the cantrip carve-out)
  const cast = Number(text);
  for (const key of UNMODELLED_UPCAST_LAYERS) {
    if (cast <= level || !ctx.read.has(ability, key) || (key === 'spell_upcast_increase_aoe_ft' && opts.placedArea)) continue;
    if (key === 'spell_upcast_additional_projectiles_created') {
      const children = ctx.read.entries(ability, 'free_ability');
      const child = children.length === 1 ? ctx.read.definition(layerListEntryUuid(children[0]).toUpperCase()) : null;
      if (child && instantProjectileCount(itemField(ability, 'item_type'), key => ctx.read.value(ability, key),
        ctx.read.value(child, 'ability_charges'), variable1) !== null) continue;
    }
    handBack(`upcast:unmodelled:${key}`);
  }
  // A supported roll must consume the growth itself; otherwise the cast would silently drop the upcast layers.
  const rollType = String(ctx.read.value(ability, 'roll_type') ?? '').toUpperCase();
  if (cast > level && (ctx.read.value(ability, 'dice_upcast') != null || ctx.read.value(ability, 'mod_upcast') != null)
      && !UPCAST_ROLL_TYPES.includes(rollType)) handBack(`upcast:roll-type:${rollType || 'none'}`);
  return cast;
}

/** spell_upcast_increase_aoe_ft on a PLACED area: the feet the radius grows by — the layer × the levels cast above the spell's own (the
 *  painted-wall twin: port/k2-geometry/painted_wall_cast.ts). 0 when the variable is not a cast level above a leveled spell's own. */
export function upcastAreaGrowthFt(read: { value: (row: Row | null, key: string) => unknown }, ability: Row, variable1: unknown): number {
  if (record(ability.computed)?.next_spell_area_growth_applied) return 0;
  const text = variable1 == null ? '' : String(variable1).trim();
  if (!/^\d{1,2}$/.test(text) || !LEVELED_TYPES.includes(String(itemField(ability, 'item_type') ?? ''))) return 0;
  const level = Math.trunc(Number(read.value(ability, 'spell_level') ?? read.value(ability, 'menta_level') ?? 0));
  const extra = Number(text) - level, growth = Number(read.value(ability, 'spell_upcast_increase_aoe_ft') ?? 0);
  return Number.isFinite(level) && level > 0 && extra > 0 && Number.isFinite(growth) && growth > 0 ? extra * growth : 0;
}

const plainInt = (value: unknown, reason: string): number => {
  let v = value;
  while (Array.isArray(v)) v = v[0];
  if (typeof v === 'number' && Number.isSafeInteger(v)) return v;
  if (typeof v === 'string' && /^-?\d+$/.test(v.trim())) return Number(v.trim());
  return handBack(reason);
};

/** upcast_roll(): the cast item's dice_upcast / mod_upcast × (cast level − spell level). No cast level (ctx.castLevel null) = a no-op. */
export function upcastRoll(ctx: JanusCtx, castItem: Row, dice: number, mod: number): { dice: number; mod: number } {
  const cast = ctx.castLevel ?? null;
  if (cast === null || !LEVELED_TYPES.includes(String(itemField(castItem, 'item_type') ?? ''))) return { dice, mod };
  const level = spellLevelOf(ctx, castItem);
  if (level <= 0) return { dice, mod };
  const diff = Math.max(0, cast - level);
  // a cast AT (or under) the spell's level adds 0 × anything (PHP still discerns the formula, to no effect) — never parse it
  if (diff === 0) return { dice, mod };
  let nextDice = dice, nextMod = mod;
  if (ctx.read.value(castItem, 'dice_upcast') != null && cast > level)
    nextDice += diff * plainInt(ctx.read.value(castItem, 'dice_upcast'), 'upcast_roll:dice-upcast-formula');
  if (ctx.read.value(castItem, 'mod_upcast') != null)
    nextMod += diff * plainInt(ctx.read.value(castItem, 'mod_upcast'), 'upcast_roll:mod-upcast-formula');
  return { dice: nextDice, mod: nextMod };
}
